import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, delay, from, Observable, of } from 'rxjs';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType, PopupRequest, RedirectRequest
} from '@azure/msal-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WINDOW } from '@frontend-mono/core';

export interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

export interface AuthToken {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly _destroying$ = inject(DestroyRef);
  private readonly msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private readonly window = inject(WINDOW);

  // BehaviorSubject to track authentication state
  private authReadySubject = new BehaviorSubject<boolean>(false);
  public authReady$ = this.authReadySubject.asObservable();

  // Track initialization state
  private initializing = false;
  private initialized = false;

  public get isFrame(): boolean {
    return this.window !== this.window.parent && !this.window.opener;
  }

  constructor() {
    this.initialize()
      .pipe(takeUntilDestroyed(this._destroying$))
      .subscribe();
    // Listen for login success events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntilDestroyed(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        console.log('Login success event received in auth service');
        const payload = result.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(payload.account);
        this.authReadySubject.next(true);
      });
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
        takeUntilDestroyed(this._destroying$)
      )
      .subscribe(() => {
        this.authReadySubject.next(false);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntilDestroyed(this._destroying$)
      )
      .subscribe(() => {
        if (!this.authReadySubject.value) {
          this.authReadySubject.next(false);
        }
      });
  }

  /**
   * Initialize the authentication service
   * Returns an Observable that completes when auth is ready
   */
  public initialize(): Observable<boolean> {
    // If already initialized or initializing, return current state
    if (this.initialized) {
      return of(true);
    }

    if (this.initializing) {
      return this.authReady$;
    }

    this.initializing = true;

    // Handle any redirect first
    return from(this.msalService.handleRedirectObservable()).pipe(
      tap(() => {
        // Check for existing accounts
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          this.msalService.instance.setActiveAccount(accounts[0]);
          this.authReadySubject.next(true);
        } else {
          this.authReadySubject.next(false);
        }
        this.initialized = true;
        this.initializing = false;
      }),
      delay(300),
      catchError(error => {
        console.error('Error during auth initialization:', error);
        this.authReadySubject.next(false);
        this.initialized = true;
        this.initializing = false;
        return of(false);
      }),
      switchMap(() => this.authReady$)
    );
  }

  /**
   * Get the current active account
   */
  public getActiveAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  public login(): void {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.msalService.loginPopup({
          ...this.msalGuardConfig.authRequest,
        } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.msalService.instance.setActiveAccount(response.account);
          });
      } else {
        this.msalService.loginPopup()
          .subscribe((response: AuthenticationResult) => {
            this.msalService.instance.setActiveAccount(response.account);
          });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.msalService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
        } as RedirectRequest);
      } else {
        this.msalService.loginRedirect();
      }
    }
  }

  public logout(): void {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.msalService.logoutPopup({
        account: this.msalService.instance.getActiveAccount(),
      });
    } else {
      this.msalService.logoutRedirect({
        account: this.msalService.instance.getActiveAccount(),
      });
    }
  }
}
